/**
 * Ticket Type Descriptions Utility for Swiss Fintech Week 2026
 * 
 * This utility provides custom HTML descriptions for each ticket type ID.
 * 
 * HOW TO ADD NEW DESCRIPTIONS:
 * 1. Find the ticket type ID from FormSettings.cs or the ticketing system
 * 2. Add a new entry in the descriptions object below with the ticket ID as the key
 * 3. Provide the HTML markup for the description (supports HTML tags)
 * 4. Use the checklist format for consistency:
 *    - Use <b> tags for section headings
 *    - Use <ul class="checklist"> for lists
 *    - Use ✔ character for checkmarks in list items
 * 
 * EXAMPLE FORMAT:
 * ticketTypeId: `
 *     <b>Section Title</b>
 *     <ul class="checklist">
 *         <li>✔ Item 1</li>
 *         <li>✔ Item 2</li>
 *     </ul>
 * `
 * 
 * The function returns null if no description is found for a ticket type,
 * which will cause the default ticket description from the API to be displayed.
 */

// Ticket type descriptions for Swiss Fintech Week 2026
// Each ticket type ID maps to its specific HTML description

export const getTicketDescription = (ticketTypeId) => {
    const descriptions = {
        // Private: Swiss Fintech Pass incl. Point Zero Forum
        879120: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
            <b>Point Zero Forum</b>
            <ul class="checklist">
                <li>✔ Industry Pass</li>
            </ul>
        `,

        // Private: Swiss Fintech Pass incl. Point Zero Forum
        890965: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,
        
        // Private: Swiss Fintech Pass excl. Point Zero Forum  (with promocode)
        879121: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,
        
        // Private: Swiss Fintech Pass excl. Point Zero Forum
        879122: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,


        // Private: Swiss Fintech Pass excl. Point Zero Forum (with promocode)
        879123: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,

        // Private: Early bird - Swiss Fintech Pass excl. Point Zero Forum
        883552: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,

        // Private: Early bird - Swiss Fintech Pass excl. Point Zero Forum (with promocode)
        883553: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,

        // Private: Swiss Fintech Pass excl.Point Zero Forum(with promocode)
        887615: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,

        
        // Start-Up: Swiss Fintech Pass excl. Point Zero Forum (with promocode)
        879125: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,

        // Media Pass: (with promocode)
        879127: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
            <span>Media access to Point Zero Forum is not included</span>
        `,

        // Sponsor: Fintech Makers Night (with promocode)
        879205: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ Fintech Makers Night</li>
            </ul>
        `,

        // Sponsor: Parnter: Swiss Fintech Pass incl. Point Zero Forum (with promocode)
        879206: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
            <b>Point Zero Forum</b>
            <ul class="checklist">
                <li>✔ Industry Pass</li>
            </ul>
        `,

        // Sponsor: Parnter: Swiss Fintech Pass excl. Point Zero Forum (with promocode)
        879207: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,

        // Sponsor: Parnter: Swiss Fintech Pass excl. Point Zero Forum (with promocode)
        879208: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
                <li>✔ Swiss FinTech Awards Night</li>
                <li>✔ Fintech Makers Night</li>
                <li>✔ Innovation Tours</li>
                <li>✔ SwissHacks Closing Ceremony</li>
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,

        // Sponsor: FuW Fintech Forum, SFW Startup Fair (with promocode)
        879209: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ FuW FinTech Forum incl. Workshops</li>
                <li>✔ SFW Startup Fair</li>
            </ul>
        `,

        // Sponsor: Swiss Fintech Awards Night (with promocode)
        879210: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ Swiss Fintech Awards Night</li>
            </ul>
        `,

        // Sponsor: Swiss Fintech Week Afterparty (with promocode)
        879211: `
            <b>Included Access</b>
            <ul class="checklist">
                <li>✔ Swiss Fintech Week Afterparty</li>
            </ul>
        `,
    };

    return descriptions[ticketTypeId] || null;
};

export default getTicketDescription;
